/**
 * A fixed pool of <video> elements, all "unlocked" by the first user gesture
 * (WebKit lifts its per-element gesture restrictions when load()/play() is
 * called on the element during a gesture). Candidates for the next node are
 * assigned to idle elements so the chosen branch starts from a warm buffer.
 */
export interface Slot {
  el: HTMLVideoElement;
  nodeId?: string;
  src?: string;
}

export class VideoPool {
  readonly slots: Slot[] = [];
  private unlocked = false;

  constructor(container: HTMLElement, size = 4) {
    for (let i = 0; i < size; i++) {
      const el = document.createElement('video');
      el.playsInline = true;
      el.setAttribute('playsinline', '');
      el.setAttribute('webkit-playsinline', '');
      el.preload = 'auto';
      el.controls = false;
      el.disablePictureInPicture = true;
      el.setAttribute('disableremoteplayback', '');
      el.muted = true; // flipped to audible inside the first gesture
      el.dataset['slot'] = String(i);
      container.appendChild(el);
      this.slots.push({ el });
    }
  }

  /** Call synchronously inside a user-gesture handler. */
  unlock(audible: boolean): void {
    for (const s of this.slots) {
      s.el.muted = !audible;
      try {
        s.el.load();
      } catch {
        /* ignore */
      }
    }
    this.unlocked = true;
  }

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  setMuted(muted: boolean): void {
    for (const s of this.slots) s.el.muted = muted;
  }

  slotFor(nodeId: string): Slot | undefined {
    return this.slots.find((s) => s.nodeId === nodeId);
  }

  /** Assign a node's clip to an idle slot (or return the existing one). */
  assign(nodeId: string, src: string, poster: string | undefined, keep: Set<string>): Slot | undefined {
    const existing = this.slotFor(nodeId);
    if (existing) return existing;
    const free = this.slots.find((s) => !s.nodeId) ?? this.slots.find((s) => s.nodeId && !keep.has(s.nodeId) && !s.el.classList.contains('active'));
    if (!free) return undefined;
    this.release(free);
    free.nodeId = nodeId;
    free.src = src;
    if (poster) free.el.poster = poster;
    else free.el.removeAttribute('poster');
    free.el.src = src;
    try {
      free.el.load();
    } catch {
      /* ignore */
    }
    return free;
  }

  release(slot: Slot): void {
    if (!slot.nodeId && !slot.src) return;
    slot.el.classList.remove('active');
    try {
      slot.el.pause();
    } catch {
      /* ignore */
    }
    slot.el.removeAttribute('src');
    slot.el.removeAttribute('poster');
    try {
      slot.el.load();
    } catch {
      /* ignore */
    }
    delete slot.nodeId;
    delete slot.src;
  }

  /** Drop every slot not in `keep` (and not the active one). */
  prune(keep: Set<string>): void {
    for (const s of this.slots) if (s.nodeId && !keep.has(s.nodeId) && !s.el.classList.contains('active')) this.release(s);
  }

  destroy(): void {
    for (const s of this.slots) {
      this.release(s);
      s.el.remove();
    }
  }
}
